import type { QuoteOutput } from '../types';
import { POLICY_ISSUANCE_FEE_BY_PARTNER, POLICY_ISSUANCE_FEE_DEFAULT } from '../constants';

/**
 * Parse a percentage string (e.g., "5%", "5.5%") to a decimal number
 */
function parsePercentage(value: string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const cleaned = String(value).replace(/%/g, '').trim();
  if (cleaned === '') return 0;
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed / 100;
}

/**
 * Parse a currency string (e.g., "$1,234.56") to a number
 */
function parseCurrency(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const cleaned = String(value).replace(/[$,]/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Parse a plain decimal string ("100.0000") to a number. Used for the
 * AGG_Insured_Lives_W_PEO_PART snapshot which is unitless (lives × peo_part).
 */
function parseDecimal(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const cleaned = String(value).replace(/[,]/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Format a number as currency string
 */
function formatCurrency(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format a number as percentage string
 */
function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

/**
 * Inputs for the recalculator. Every field is optional; anything omitted
 * falls back to the value already on `output`. Pass only the field(s) the
 * user actually edited.
 */
export interface RecalculateEdits {
  underwriting_discretion?: string;
  business_discretion?: string;
  business_discretion_percentage_for_sas?: string;
  business_discretion_percentage_for_lms?: string;
  /** Empty string / null / "0%" → use Manual Parento %. */
  parento_commission_percentage_override?: string | null;
  /** Empty string → use Manual Reinsurance Partner. */
  reinsurance_partner_override?: string;
}

/**
 * Recompute every FE-derivable field on a QuoteOutput from the snapshot
 * (rater-written values) plus the user's edits. Implements the full
 * topological graph specified in the QuoteUI-Rater mapping CSV:
 *
 *   Quoted Parento %  = IF override != 0 THEN override ELSE Manual %
 *   PLR_WITH_ADJ      = PLR + Manual_% − Quoted_%
 *   Manual Premium    = agg_manual_premium × PLR / PLR_WITH_ADJ
 *   Quoted Premium    = Manual Premium × (1 + UD) × (1 + BD)        ← multiplicative
 *   Surplus Lines Tax = Quoted Premium × SL_%
 *   Stamping Fee      = Quoted Premium × Stamping_%
 *   Quoted Rein Part. = override or Manual Rein. Partner
 *   Policy Iss. Fee   = partner-fee lookup (Greenlight=$250, else $0)
 *   Quoted Prem w/Tax = Quoted Premium + SL Tax + Stamping + Policy Iss.
 *   Parento Comm $    = Quoted Premium × Quoted Parento %           ← uses Quoted, not w/Tax
 *   Quoted SaaS Fee   = Manual SaaS Fee × (1 + SaaS BD)
 *   Quoted LMS Fee    = Manual LMS Fee × (1 + LMS BD) × (1 + LMS Strat Adj)
 *   TPC w/o           = Quoted Premium + Quoted SaaS Fee + Quoted LMS Fee
 *   Tax+Stamp+Iss     = SL Tax + Stamping + Policy Iss.             ← per rater
 *   TPC With          = TPC w/o + Tax+Stamp+Iss
 *   Tax Credit App $  = tax_credit_app_premium_percent × Quoted Premium
 *   Tax Credit $      = tax_credit_app_premium_percent × tax_credit_% × Quoted Premium
 *   Net Cost          = TPC w/o − Tax Credit
 *   Net Cost w/ Tax   = TPC With − Tax Credit
 *   *_pepm            = value / covered_lives / 12
 *
 * Returns a Partial<QuoteOutput> ready to PATCH to the BE.
 */
export function recalculateOutputFields(
  output: QuoteOutput,
  edits: RecalculateEdits = {},
): Partial<QuoteOutput> {
  // --- Snapshots (rater-written, never edited by the user) ---
  const aggManualPremium = parseCurrency(output.agg_manual_premium);
  const manualSasFee = parseCurrency(output.manual_sas_fee);
  const manualLmsFee = parseCurrency(output.manual_lms_fee);
  const slTaxPct = parsePercentage(output.surplus_lines_tax_percentage);
  const stampingPct = parsePercentage(output.stamping_fee_tax_percentage);
  const plr = parsePercentage(output.pricing_loss_ratio);
  const manualParentoPct = parsePercentage(output.manual_parento_commission_percentage);
  const taxCreditPct = parsePercentage(output.tax_credit_percent);
  const taxCreditAppPct = parsePercentage(output.tax_credit_app_premium_percent);
  const manualReinsurance = output.manual_reinsurance_partner || '';
  // PEPM denominator (item 32): covered_lives (OUT_KM_COV_LIVES). In the rater
  // OUT_KM_COV_LIVES = AGG_Insured_Lives_W_PEO_PART, so this is the canonical
  // source for the same value.
  const coveredLives = parseDecimal(output.covered_lives);
  // PEO strategy adjustments (item 31a). Rater applies them as extra
  // multiplicative factors on Quoted Premium / Quoted SaaS Fee, but it GATES
  // them on PEO group:
  //   OUT_PREM_STRAT_ADJ = IF(OUT_PEO_GROUP <> "None", PEO_PREM_STRAT_ADJ, 0)
  // Mirror that gate here so non-PEO quotes get a factor of exactly 1
  // (Quoted = Manual × (1+UD) × (1+BD)), regardless of any stale value left
  // in strategy_adjustment_* — without the gate a non-PEO quote like q236
  // could read a non-zero stored adjustment and inflate Quoted Premium.
  // Stored ×100 (percentage) since migration 0032, so parsePercentage gives
  // the right decimal for genuine PEO quotes.
  const peoGroup = (output.peo_group_output || '').trim();
  const peoActive = peoGroup !== '' && peoGroup.toLowerCase() !== 'none';
  const premStratAdj = peoActive ? parsePercentage(output.strategy_adjustment_premium) : 0;
  const sasStratAdj = peoActive ? parsePercentage(output.strategy_adjustment_sas) : 0;
  // Rating Engine 1.3.3 defines OUT_LMS_STRAT_ADJ as a literal 0 rather than
  // the PEO-gated formula the SaaS one uses, so no PEO gate here — read it as
  // given, and the factor collapses to 1 until the actuary makes it dynamic.
  const lmsStratAdj = parsePercentage(output.strategy_adjustment_lms);

  // --- Edits (fall back to existing value when not provided) ---
  const udRaw = edits.underwriting_discretion ?? output.underwriting_discretion ?? '0%';
  const bdRaw = edits.business_discretion ?? output.business_discretion ?? '0%';
  const sasBdRaw =
    edits.business_discretion_percentage_for_sas ??
    output.business_discretion_percentage_for_sas ??
    '0%';
  const lmsBdRaw =
    edits.business_discretion_percentage_for_lms ??
    output.business_discretion_percentage_for_lms ??
    '0%';
  const overrideRaw =
    'parento_commission_percentage_override' in edits
      ? edits.parento_commission_percentage_override
      : (output.parento_commission_percentage_override ?? null);
  const reinOverrideRaw =
    edits.reinsurance_partner_override ??
    output.reinsurance_partner_override ??
    '';

  const ud = parsePercentage(udRaw);
  const bd = parsePercentage(bdRaw);
  const sasBd = parsePercentage(sasBdRaw);
  const lmsBd = parsePercentage(lmsBdRaw);
  const overrideNum = parsePercentage(overrideRaw);

  // --- Derived ---
  // Quoted Parento %: rater rule IF override="" THEN Manual ELSE override.
  // We treat any value that parses to 0 as "no override" so the cleared/zero
  // input falls back to Manual %.
  const quotedParentoPct = overrideNum !== 0 ? overrideNum : manualParentoPct;

  // PLR_WITH_ADJ = PLR + Manual − Quoted (per OUT_RF_PLR_WITH_ADJ formula).
  const plrWithAdj = plr + manualParentoPct - quotedParentoPct;

  // Manual Premium = AGG_Manual_Premium × PLR / PLR_WITH_ADJ.
  // Guard division-by-zero: when no override is applied PLR_WITH_ADJ = PLR
  // and this collapses to aggManualPremium (the invariant base).
  const manualPremium = plrWithAdj === 0 ? aggManualPremium : (aggManualPremium * plr) / plrWithAdj;

  // Quoted Premium = Manual Premium × (1+UD) × (1+BD) × (1+PremStratAdj).
  // MULTIPLICATIVE — matches the v1.2 rater's OUT_Quoted_Premium formula
  // (item 31a added the strategy-adjustment factor). PremStratAdj is 0 for
  // non-PEO quotes, so this collapses to the plain UD/BD product there.
  const quotedPremium = manualPremium * (1 + ud) * (1 + bd) * (1 + premStratAdj);

  const surplusLinesTax = quotedPremium * slTaxPct;
  const stampingFee = quotedPremium * stampingPct;

  // Quoted Reinsurance Partner: override wins if non-empty, else Manual.
  const quotedReinsurance = reinOverrideRaw && reinOverrideRaw.length > 0
    ? reinOverrideRaw
    : manualReinsurance;
  const policyIssuanceFee =
    POLICY_ISSUANCE_FEE_BY_PARTNER[quotedReinsurance] ?? POLICY_ISSUANCE_FEE_DEFAULT;

  const quotedPremiumWithTaxes =
    quotedPremium + surplusLinesTax + stampingFee + policyIssuanceFee;

  // Parento Commission $ uses QUOTED PREMIUM (per OUT_Parento_Comm formula),
  // NOT quoted_premium_with_taxes — the old code was wrong here.
  const parentoCommission = quotedPremium * quotedParentoPct;

  // Quoted SaaS Fee = Manual SaaS Fee × (1+SaaS BD) × (1+SaaS StratAdj),
  // matching the v1.2 rater's OUT_Quoted_SAS_FEE (item 31a). SaaS StratAdj
  // is 0 for non-PEO quotes.
  const quotedSasFee = manualSasFee * (1 + sasBd) * (1 + sasStratAdj);

  // Quoted LMS Fee = Manual LMS Fee × (1+LMS BD) × (1+LMS StratAdj), matching
  // 1.3.3's OUT_Quoted_LMS_FEE. On a No-LMS variant the rater zeroes the
  // manual fee (SAS Fee Calculation!B40), so this whole block collapses to 0.
  const quotedLmsFee = manualLmsFee * (1 + lmsBd) * (1 + lmsStratAdj);

  // 1.3.3 folded LMS into the total: OUT_TPC_Without is now
  // LMS + SaaS + Premium (it was SaaS + Premium in v1.2).
  const tpcWithout = quotedPremium + quotedSasFee + quotedLmsFee;
  // Tax/Stamping/Issuance includes the policy issuance fee per rater
  // (OUT_Tax_And_SF = Stamping + Surplus + Policy Issuance).
  const taxAndStampingFee = surplusLinesTax + stampingFee + policyIssuanceFee;
  const tpcWith = tpcWithout + taxAndStampingFee;

  // Tax Credits (rater formulas at OUT_TC_APP_PREM, OUT_TC_TAX_CRED).
  const taxCreditAppPremium = taxCreditAppPct * quotedPremium;
  const taxCreditAmount = taxCreditAppPct * taxCreditPct * quotedPremium;
  const netCost = tpcWithout - taxCreditAmount;
  const netCostWithTaxFee = tpcWith - taxCreditAmount;

  // PEPM: rater denominator is covered lives × 12 (item 32).
  const denom = coveredLives * 12;
  const safeDiv = (n: number) => (denom === 0 ? 0 : n / denom);
  const quotedPremiumPepm = safeDiv(quotedPremium);
  const quotedSasFeePepm = safeDiv(quotedSasFee);
  const quotedLmsFeePepm = safeDiv(quotedLmsFee);
  // Both PEPM roll-ups gained the LMS term in 1.3.3:
  //   OUT_PEPM_QP_SAS            = PEPM_LMS + PEPM_SAS + PEPM_QP
  //   OUT_PEPM_Parento_Total_Rev = PEPM_Comm + PEPM_SAS + PEPM_LMS
  const quotedPremiumSasFeePepm = quotedPremiumPepm + quotedSasFeePepm + quotedLmsFeePepm;
  const quotedPremiumSasFeePepmWithTaxFee = safeDiv(tpcWith);
  const parentoCommissionsPepm = safeDiv(parentoCommission);
  const parentoTotalRevenuePepm =
    parentoCommissionsPepm + quotedSasFeePepm + quotedLmsFeePepm;

  return {
    // Echo back the user's edits so the FE form stays in sync with what was
    // PATCHed. (The store also writes these to editableOutputs but echoing
    // here makes the function self-contained.)
    underwriting_discretion: udRaw,
    business_discretion: bdRaw,
    business_discretion_percentage_for_sas: sasBdRaw,
    business_discretion_percentage_for_lms: lmsBdRaw,
    parento_commission_percentage_override: overrideRaw,
    reinsurance_partner_override: reinOverrideRaw,
    // Derived values (string-formatted to match the BE serializer's shape).
    manual_premium: formatCurrency(manualPremium),
    pricing_loss_ratio_with_adj: formatPercentage(plrWithAdj * 100),
    parento_commission_percentage: formatPercentage(quotedParentoPct * 100),
    quoted_reinsurance_partner: quotedReinsurance,
    policy_issuance_fee: formatCurrency(policyIssuanceFee),
    quoted_premium: formatCurrency(quotedPremium),
    surplus_lines_tax: formatCurrency(surplusLinesTax),
    stamping_fee: formatCurrency(stampingFee),
    quoted_premium_with_taxes: formatCurrency(quotedPremiumWithTaxes),
    parento_commission: formatCurrency(parentoCommission),
    quoted_sas_fee: formatCurrency(quotedSasFee),
    quoted_lms_fee: formatCurrency(quotedLmsFee),
    total_program_cost_without_tax_and_stamping_fee: formatCurrency(tpcWithout),
    tax_and_stamping_fee: formatCurrency(taxAndStampingFee),
    total_program_costs_with_tax_stamping_fee: formatCurrency(tpcWith),
    tax_credit_app_premium: formatCurrency(taxCreditAppPremium),
    tax_credit_amount: formatCurrency(taxCreditAmount),
    net_cost_after_tax_credit: formatCurrency(netCost),
    net_cost_after_tax_credit_with_tax_fee: formatCurrency(netCostWithTaxFee),
    quoted_premium_pepm: formatCurrency(quotedPremiumPepm),
    quoted_sas_fee_pepm: formatCurrency(quotedSasFeePepm),
    quoted_lms_fee_pepm: formatCurrency(quotedLmsFeePepm),
    quoted_premium_sas_fee_pepm: formatCurrency(quotedPremiumSasFeePepm),
    quoted_premium_sas_fee_pepm_with_tax_fee: formatCurrency(quotedPremiumSasFeePepmWithTaxFee),
    parento_commissions_pepm: formatCurrency(parentoCommissionsPepm),
    parento_total_revenue_pepm: formatCurrency(parentoTotalRevenuePepm),
  };
}

/**
 * Validate that discretion percentages are > -100% individually and as a pair.
 * Returns null when valid, or an error message.
 *
 * Use {@link getDiscretionPairErrors} when you need to know which field(s) are
 * at fault so the UI can flag them all (e.g., sum-violation flags BOTH inputs).
 */
export function validateDiscretionPair(
  underwritingDiscretion: string | number | null | undefined,
  businessDiscretion: string | number | null | undefined,
): string | null {
  const errors = getDiscretionPairErrors(underwritingDiscretion, businessDiscretion);
  return errors.underwriting_discretion ?? errors.business_discretion ?? null;
}

/**
 * Compute per-field validation errors for the UD + BD pair.
 *
 * Rule (per PDF): each field individually and their sum must be > -100%.
 * A sum violation flags BOTH fields so the user sees the dependency.
 */
export function getDiscretionPairErrors(
  underwritingDiscretion: string | number | null | undefined,
  businessDiscretion: string | number | null | undefined,
): { underwriting_discretion?: string; business_discretion?: string } {
  const uw = typeof underwritingDiscretion === 'number'
    ? underwritingDiscretion
    : parseFloat(String(underwritingDiscretion ?? '').replace(/%/g, '').trim());
  const bus = typeof businessDiscretion === 'number'
    ? businessDiscretion
    : parseFloat(String(businessDiscretion ?? '').replace(/%/g, '').trim());

  const errors: { underwriting_discretion?: string; business_discretion?: string } = {};

  // Per PDF spec: block strictly below -100% (the screenshot shows -150% and
  // -200% as bad; -100% itself is the boundary and is allowed). Any positive
  // value passes.
  const uwInvalid = !isNaN(uw) && uw < -100;
  const busInvalid = !isNaN(bus) && bus < -100;

  if (uwInvalid) {
    errors.underwriting_discretion = 'Underwriting Discretion cannot be less than -100%.';
  }
  if (busInvalid) {
    errors.business_discretion = 'Business Discretion cannot be less than -100%.';
  }
  // Only escalate to a sum-error (which flags BOTH inputs) when neither is
  // individually invalid. Otherwise the sum error is just noise on top of an
  // already-pinpointed offender.
  if (!uwInvalid && !busInvalid && !isNaN(uw) && !isNaN(bus) && uw + bus < -100) {
    const sumMessage =
      'The sum of Underwriting Discretion and Business Discretion cannot be less than -100%.';
    errors.underwriting_discretion = sumMessage;
    errors.business_discretion = sumMessage;
  }
  return errors;
}

/**
 * Validate a standalone Business Discretion percentage (SaaS or LMS).
 * Per PDF: block strictly below -100% (e.g., -140% in the example image).
 * -100% itself is the boundary and is allowed. Positive and modest negatives pass.
 */
export function validateSingleDiscretion(
  value: string | number | null | undefined,
  label = 'SaaS',
): string | null {
  const v = typeof value === 'number'
    ? value
    : parseFloat(String(value ?? '').replace(/%/g, '').trim());
  if (!isNaN(v) && v < -100) {
    return `Business Discretion % for ${label} cannot be less than -100%.`;
  }
  return null;
}

/**
 * Compute the FE-side quoted reinsurance partner (mirrors the rater's formula:
 * IF override == "" THEN manual ELSE override).
 */
export function quotedReinsurancePartner(
  manual: string | null | undefined,
  override: string | null | undefined,
): string {
  const o = (override ?? '').trim();
  if (o) return o;
  return (manual ?? '').trim();
}

/**
 * Helper to format output values for display
 */
export function formatOutputValue(value: number | string | null | undefined, type: 'currency' | 'percentage' | 'number' = 'number'): string {
  if (value === null || value === undefined) return '-';
  
  if (type === 'currency') {
    return formatCurrency(parseCurrency(value));
  }
  
  if (type === 'percentage') {
    if (typeof value === 'string' && value.includes('%')) {
      return value;
    }
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return '0.00%';
    if (num < 1 && num > 0) {
      return formatPercentage(num * 100);
    }
    return formatPercentage(num);
  }
  
  return String(value);
}

